/** Section 15: moving a leeway Visit outside its compliance window is allowed only after the person confirms it. */
import { VisitApprovalStandingStatus, VisitBrowseRow, visitSpacingWarning, visitWindowWarning } from '@jms/model';
import { WorkBuddyDialogApi } from '@workbuddy/ui/v2/blocks/dialog';
import { ReasonPromptComponent } from '@workbuddy/ui/v2/blocks/dialog/reason.prompt/reason.prompt.component';
import { DateTime } from 'luxon';

export type VisitWindowSubject = Pick<VisitBrowseRow, 'localDate' | 'timezone' | 'windowStart' | 'windowEnd'> & Partial<Pick<VisitBrowseRow, 'approvalStatus' | 'spacing'>>;

/** Spacing is a commitment's rhythm, so only approved work (or a line that needs none) is warned about it. */
const SPACED_STATUSES: ReadonlySet<VisitApprovalStandingStatus | undefined> = new Set([VisitApprovalStandingStatus.Approved, VisitApprovalStandingStatus.AutoApproved]);

/**
 * Resolves `allowOutsideWindow` for the dates request: false when nothing needs saying, true once a window
 * breach is confirmed, null when backed out. Section 16 spacing joins the same dialog as a warning only:
 * confirming it sends nothing, because the server never refuses a spacing breach.
 */
export async function confirmVisitWindow(dialogs: WorkBuddyDialogApi, row: VisitWindowSubject, startDate: Date): Promise<boolean | null> {

  const warning = visitWindowWarning(row, startDate);
  const spacing = SPACED_STATUSES.has(row.approvalStatus)
    ? visitSpacingWarning(row.spacing, DateTime.fromJSDate(startDate, { zone: row.timezone || 'local' }).toISODate() ?? '')
    : undefined;
  if (!warning && !spacing) return false;
  const title = warning ? 'Outside the compliance window' : 'Visit spacing';
  const result = await dialogs.modal<string>(ReasonPromptComponent, {
    width: 'min(480px, calc(100vw - 40px))',
    inputs: { title, description: [warning, spacing && `${spacing}.`].filter(Boolean).join(' '), confirmLabel: 'Move anyway', cancelLabel: 'Cancel', placeholder: '', multiline: true, validate: null, optionalReason: true },
    aria: { label: title },
  });
  if (result === undefined) return null;

  return !!warning;
}
